import {
  EntityPresentationApi,
  EntityRefPresentation,
} from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

/**
 * Wraps a real EntityPresentationApi so a bad ref never crashes the whole
 * page. @backstage/plugin-catalog's DefaultEntityPresentationApi already
 * falls back to `{ primaryTitle: entityRef, entityRef }` if *rendering*
 * throws, but that safety net does not cover its own ref-parsing step
 * (`#getEntityForInitialRender`), which calls `parseEntityRef` with no
 * default kind/namespace and therefore throws — taking the whole page down
 * via the error boundary — for any unqualified ref string.
 *
 * Hit in practice via @backstage/plugin-org's OwnershipCard: it builds the
 * Catalog page's `?filters[owners]=` link from the *humanized* owner name
 * (e.g. "portfolio-and-api", not "group:default/portfolio-and-api"), and
 * @backstage/plugin-catalog-react's EntityOwnerPicker (default "owners-only"
 * mode) round-trips that bare string straight into this API with no
 * default-kind context. Both are upstream bugs we can't patch directly, and
 * this reproduces for every team, not just entities with plain (unqualified)
 * `owner:` fields in their own catalog-info.yaml.
 *
 * Extends the same fallback the library already uses one level up, rather
 * than guessing a default kind (e.g. "group") ourselves — the ref is
 * genuinely ambiguous once qualification is lost, so showing it as-is is the
 * honest degradation instead of an assumption that could mislabel a User or
 * other kind.
 */
export class ResilientEntityPresentationApi implements EntityPresentationApi {
  constructor(private readonly inner: EntityPresentationApi) {}

  forEntity(
    entityOrRef: Entity | string,
    context?: { defaultKind?: string; defaultNamespace?: string },
  ): EntityRefPresentation {
    try {
      return this.inner.forEntity(entityOrRef, context);
    } catch {
      const entityRef =
        typeof entityOrRef === 'string'
          ? entityOrRef
          : stringifyEntityRef(entityOrRef);
      const snapshot = { entityRef, primaryTitle: entityRef };
      return { snapshot, promise: Promise.resolve(snapshot) };
    }
  }
}
